import type {GridStateManager} from 'shared/ui-widgets/grid/state/state-management';
import type {Row, Table} from '@tanstack/svelte-table';
import type {Logger} from 'src/core/logging/types';
import type {ToastMessageService} from 'shared/components/kp-toast-messages/toast-message.service';
import type { GridCommand} from 'shared/ui-widgets/grid/types';
import {CellCoordinates} from 'shared/ui-widgets/grid/types';
import type {ClipboardCellData} from 'shared/ui-widgets/grid/clipboard/clipboard';
import {createClipBoardItem, resolveClipboardData} from 'shared/ui-widgets/grid/clipboard/clipboard';
import {getCellValueByCoordinates, getColumnByIndex, getRowByIndex} from 'shared/ui-widgets/grid/utils';
import {assertTrue, assertUnreachable, cleanup, exists, memoizeDebounce} from 'shared/utils/custom-utils';
import type {GridField, RecordRow, SimpleGridFieldValue} from 'src/features/record-grid/lib/types';
import type {BehaviorSubject, Subscription} from 'rxjs';
import {tap, Subject, map} from 'rxjs';
import {anyMatches, byIdOf, findFirstIndex, removeAll, replaceAll} from 'shared/utils/array-utils';
import type {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
import type {Readable, Unsubscriber} from 'svelte/store';
import type {SearchManager} from 'src/features/search/search-manager/search-manager';
import {sleep} from 'shared/value-editors/test-utils/utils';
import {COLUMN_TYPES} from 'src/features/record-grid/lib/constants';
import {resolveGridFieldAttributeValue} from 'src/features/record-grid/lib/grid-utils';
import type {FieldId, FieldTypeId, Fond, SetFieldValueRequest} from 'typings/portaro.be.types';
import {tick} from 'svelte';
import {
    deleteFieldByFieldIdRecursive,
    setFieldByFieldIdRecursive
} from 'src/features/record-grid/lib/grid-fields';
import type {LocalizeFunction} from 'core/types';
import type { PlainTextParsingContext} from 'src/features/record-grid/lib/clipboard';
import {marshallCellValue, unmarshallCellValue} from 'src/features/record-grid/lib/clipboard';
import {generateIdOfInitialField} from 'src/features/record-grid/components/cells/data-cell/utils';
import {asFieldTypeId} from 'shared/utils/record-field-utils';
import {bufferDebounce, shareWithRefCount} from 'shared/utils/observables-utils';

export class RecordGridDataManager {

    private table: Table<RecordRow>;
    private refreshRowRequest$ = new Subject<RecordRow>();
    private readonly refreshRequestSubscription: Subscription;
    private readonly commandsSubscription: Subscription;
    private readonly tableUnsubscriber: Unsubscriber;

    constructor(private table$: Readable<Table<RecordRow>>,
                private stateManager: GridStateManager,
                private flatTableData$: BehaviorSubject<RecordRow[]>,
                private command$: Subject<GridCommand>,
                private recordGridService: RecordGridService,
                private searchManager: SearchManager<RecordRow>,
                private localize: LocalizeFunction,
                private locale: string,
                private logger: Logger,
                private toastMessageService: ToastMessageService) {
        this.tableUnsubscriber = this.table$.subscribe((table) => this.table = table);

        this.refreshRequestSubscription = this.refreshRowRequest$.pipe(
            shareWithRefCount(),
            bufferDebounce(500),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap((rows) => this.refreshRows(rows))
        ).subscribe();

        this.commandsSubscription = this.command$.subscribe((command) => this.processCommand(command));
    }

    public disconnect() {
        cleanup(this.refreshRequestSubscription, this.commandsSubscription, this.tableUnsubscriber);
    }

    public processCommand(command: GridCommand) {
        switch (command.type) {
            case 'delete':
                this.deleteCell(command.target);
                break;
            case 'copy':
                this.copyCell(command.target);
                break;
            case 'paste':
                this.pasteCell(command.target);
                break;
            case 'cut':
                this.cutCell(command.target);
                break;
            case 'rollback-cell-change':
                break;
            default:
                assertUnreachable(command.type)
        }
    }

    public async copyCell(sourceCell: CellCoordinates) {
        try {
            const cellData = this.extractCellData(sourceCell);
            const clipboardItem = createClipBoardItem(cellData);
            await navigator.clipboard.write([clipboardItem]);
        } catch (error) {
            this.logger.error('Error during copying from grid', error);
            this.toastMessageService.showError('Error during copying from grid');
        }
    }

    public async pasteCell(targetCell: CellCoordinates) {
        try {
            const clipboardItems = await navigator.clipboard.read();
            const clipboardData = await resolveClipboardData(clipboardItems);
            if (exists(clipboardData)) {
                const targetColumn = getColumnByIndex(this.table, targetCell.columnIndex);
                const ctx: PlainTextParsingContext = {
                    datatype: targetColumn.columnDef.meta.datatype,
                    editorType: targetColumn.columnDef.meta.editorType,
                    localize: this.localize,
                    locale: this.locale
                }
                const fieldValues = unmarshallCellValue(clipboardData, ctx);
                const row = getRowByIndex(this.table, targetCell.rowIndex).original;
                const fieldId = generateIdOfInitialField(asFieldTypeId(targetColumn.id));

                this.updateTableDataCellState(row, fieldId, fieldValues.at(0));
                await this.updateServerCellState(row, fieldId, fieldValues.at(0));
                this.requestRowRefresh(row);
            }
        } catch (error) {
            this.logger.error('Error during pasting values to grid', error);
            this.toastMessageService.showError('Error during pasting values to grid');
        }
    }

    public async cutCell(sourceCell: CellCoordinates) {
        await this.copyCell(sourceCell);
        await this.deleteCell(sourceCell);
    }


    public async deleteCell(targetCell: CellCoordinates) {
        const row = getRowByIndex(this.table, targetCell.rowIndex).original;
        const fields = getCellValueByCoordinates<RecordRow, GridField<SimpleGridFieldValue>[]>(this.table, targetCell);

        fields.forEach((field) => this.deleteCellValueFormTableData(row, field.fieldId));

        try {
            await Promise.all(fields.map((field) => this.deleteCellValueOnServer(row, field.fieldId)));
        } catch (error) {
            this.logger.error('Error during deleting values from grid', error);
            this.toastMessageService.showError('Error during deleting values from grid');
        } finally {
            this.requestRowRefresh(row);
        }
    }

    public requestRowRefresh(recordRow: RecordRow) {
        this.refreshRowRequest$.next(recordRow);
    }

    public async createRow(selectedSubFond: Fond) {
        const initialValues = this.createInitialValues();
        const newRecordRow = await this.recordGridService.createNewRow(selectedSubFond, initialValues);
        this.flatTableData$.next([...this.flatTableData$.value, newRecordRow]);
        await tick();
        const requiredColumn = this.findFirstRequiredColumn(this.table, newRecordRow.fond.id);
        if (exists(requiredColumn)) {
            const targetCoordinates = new CellCoordinates({
                rowIndex: this.table.getRowModel().rows.length - 1,
                columnIndex: requiredColumn.getIndex(requiredColumn.getIsPinned() || undefined)
            });
            this.stateManager.forceEditModeIn(targetCoordinates);
        }
    }

    public async publishNewRow(row: Row<RecordRow>) {
        const newRow = await this.recordGridService.publishNewRow(row.original);
        this.flatTableData$.next(replaceAll(this.flatTableData$.value, byIdOf(newRow), newRow));
        // fade out newly added row if it shouldn't be in current page + let appserver index new record
        this.refreshPageDebounced(this.searchManager.getPagination().getPaginationDataObject().pages.at(-1).pageNumber);
    }

    public async deleteRow(row: Row<RecordRow>) {
        this.flatTableData$.next(removeAll(this.flatTableData$.value, byIdOf(row.original)));

        if (row.original.isDraft) {
            await this.recordGridService.deleteDraftRow(row.original);
        } else {
            await this.recordGridService.deleteRow(row.original);
        }
    }

    public async duplicateRow(row: Row<RecordRow>) {
        const newRecordRow = await this.recordGridService.duplicateRow(row.original);
        this.flatTableData$.next([...this.flatTableData$.value, newRecordRow]);
        await tick();
        const requiredColumn = this.findFirstRequiredColumn(this.table, newRecordRow.fond.id);
        if (exists(requiredColumn)) {
            const targetCoordinates = new CellCoordinates({
                rowIndex: this.table.getRowModel().rows.length - 1,
                columnIndex: requiredColumn.getIndex(requiredColumn.getIsPinned() || undefined)
            });
            this.stateManager.forceEditModeIn(targetCoordinates);
        }
    }

    public updateTableDataCellState(row: RecordRow, fieldId: FieldId, value: SimpleGridFieldValue) {
        if (exists(value)) {
            this.editCellValueInTableData(row, fieldId, {...value, fieldId});
        } else {
            this.deleteCellValueFormTableData(row, fieldId);
        }
    }

    public async updateServerCellState(row: RecordRow, fieldId: FieldId, value: SimpleGridFieldValue) {
        if (exists(value)) {
            await this.editCellValueOnServer(row, fieldId, value);
        } else {
            await this.deleteCellValueOnServer(row, fieldId);
        }
    }

    private editCellValueInTableData(row: RecordRow, fieldId: FieldId, value: GridField) {
        const index = findFirstIndex(this.flatTableData$.value, byIdOf(row));
        assertTrue(index > 0, `Record row was not found by ${row.id}`);
        const updatedRow = structuredClone(this.flatTableData$.value.at(index));
        setFieldByFieldIdRecursive(updatedRow, fieldId, value);
        this.flatTableData$.next(this.flatTableData$.value.toSpliced(index, 1, updatedRow));
    }

    private deleteCellValueFormTableData(row: RecordRow, fieldId: FieldId) {
        const index = findFirstIndex(this.flatTableData$.value, byIdOf(row));
        assertTrue(index > 0, `Record row was not found by ${row.id}`);
        const updatedRow = structuredClone(this.flatTableData$.value.at(index));
        deleteFieldByFieldIdRecursive(updatedRow, fieldId);

        this.flatTableData$.next(this.flatTableData$.value.toSpliced(index, 1, updatedRow));
    }

    private async editCellValueOnServer(row: RecordRow, fieldId: FieldId, value: SimpleGridFieldValue) {
        const isDraft = row.isDraft;
        if (isDraft) {
            await this.recordGridService.editDraftCell(row, fieldId, value);
        } else {
            await this.recordGridService.editCell(row.id, fieldId, value);
        }
    }

    private async deleteCellValueOnServer(row: RecordRow, fieldId: FieldId) {
        const isDraft = row.isDraft;
        if (isDraft) {
            await this.recordGridService.deleteDraftCell(row, fieldId);
        } else {
            await this.recordGridService.deleteCell(row.id, fieldId);
        }
    }

    private extractCellData(coordinates: CellCoordinates): ClipboardCellData {
        const data = getCellValueByCoordinates<RecordRow, GridField<SimpleGridFieldValue>[]>(this.table, coordinates);
        return marshallCellValue(data);
    }

    private async refreshRows(recordRows: RecordRow[]) {
        const uniqueRowsMap = new Map(recordRows.map((row) => [row.id, row]));

        const refreshedRows: RecordRow[] = await this.recordGridService.loadRows([...uniqueRowsMap.keys()]);

        this.flatTableData$.pipe(map((data) => {
            let result = data
            refreshedRows.forEach((refreshedRow) => {
                result = replaceAll(result, byIdOf(refreshedRow), {...refreshedRow, editationId: uniqueRowsMap.get(refreshedRow.id).editationId})
            })
            return result;
        }));

        if (anyMatches(recordRows, (row) => row.isDraft)) {
            return;
        }

        const pagesToRefresh = Array.from(new Set(recordRows.map((recordRow) => this.getPageNumberOfRecord(recordRow))));
        for (const pageToRefresh of pagesToRefresh) {
            this.refreshPageDebounced(pageToRefresh);
            await sleep(500); // TODO: wait for page to reload
        }
    }

    private getPageNumberOfRecord(recordRow: RecordRow): number {
        const pagination = this.searchManager.getPagination();
        const loadedRows = pagination.getPaginationDataObject().items;
        const rowIndex = findFirstIndex(loadedRows, byIdOf(recordRow));
        return Math.floor(rowIndex / pagination.getPaginationDataObject().pageSize) + 1;
    }

    private refreshPage(pageNumber: number) {
        if (this.dataContainsDrafts()) {
            return;
        }
        this.searchManager.getPagination().reloadPage(pageNumber);
    }
    private refreshPageDebounced = memoizeDebounce(this.refreshPage.bind(this), 3000, undefined, (pageNumber) => pageNumber);

    private findFirstRequiredColumn(table: Table<RecordRow>, fondId: number) {
        return table.getAllFlatColumns()
            .filter((column) => column.columnDef.meta.columnType === COLUMN_TYPES.DATA_COLUMN)
            .find((column) => resolveGridFieldAttributeValue(column.columnDef.meta.required, fondId));
    }

    private createInitialValues() {
        const initialValuesEntries: [FieldTypeId, SetFieldValueRequest][] = [];

        if (exists(this.searchManager.getStaticParams().fieldId)) {
            initialValuesEntries.push([this.searchManager.getStaticParams().fieldId, {recordId: this.searchManager.getStaticParams().recordRelatedRecord}])
        }
        return initialValuesEntries.length > 0 ? Object.fromEntries(initialValuesEntries) : undefined;
    }

    private dataContainsDrafts() {
        return this.flatTableData$.value.some((recordRow) => recordRow.isDraft);
    }
}